/**
 * M4 — "Pick your read" chip groups + answer key + sample copy.
 *
 * Verbatim from the original prototype `CONTENT.m4Analyze.step2.groups`
 * (content.js L339-372), `M4_PICK_ANSWER_KEY` (app.js L1670-1676),
 * `SAMPLE_ANALYSES` (app.js L395-421), and `CONTENT.m4Analyze.takeSamples`
 * (content.js L378-385).
 *
 * Grading (against M4_PICK_ANSWER_KEY): the `revenue` and `margin` rows are
 * objectively gradeable (green "Nailed it." / red "Not the strongest read. Try
 * another." — wrong never blocks). The `riskOpp` row is interpretive → a neutral
 * "Judgment call" note, never graded red.
 *
 * Source of truth: docs/EXPERIENCE-MODEL.md §5b.
 */

import type { CompanyId } from './companies';

// ---------------------------------------------------------------------------
// Static screen copy (intro + selection + activity headings + complete)
// Verbatim from Figma 40:9596 / 40:9620 / 40:9681 / 40:9610 (the 40:93xx Playground
// series). Not in the original `CONTENT` object — these are Vinicius's framing strings.
// ---------------------------------------------------------------------------

/** m4-intro framing copy (Figma 40:9596). */
export const M4_INTRO = {
  pill: 'Module 4 of 4',
  headline: 'Your first real company analysis',
  body: [
    "NovaTech was practice. Now you'll pick a real public company, read its three-year income statement, and form your own view alongside one we generated for you.",
    "The point isn't to match the analysis. It's to develop your own, and notice where you see it differently. That's the whole job.",
  ],
} as const;

/** m4-selection framing copy (Figma 40:9620). */
export const M4_SELECTION = {
  headline: 'Pick a company',
  subhead:
    'Each comes with a real 3-year income statement from their public filings. No NDA needed: these are public companies.',
  modulePill: 'Module 4 · Real company analysis',
} as const;

/** m4-activity static section copy (Figma 40:9681). The company-specific numbers/copy come from
 * `COMPANIES` + `SAMPLE_ANALYSES` + `TAKE_SAMPLES`; only the static labels live here. */
export const M4_ACTIVITY = {
  // Section 1 — read the sample analysis
  sampleHeading: 'Read the sample analysis',
  sampleTag: 'Run analysis',
  sampleHelper: 'A curated 3-bullet read on this company — editorial, not a live model. Takes about 8 seconds.',
  sampleFooter: 'Want to go deeper? Push back on this read with your own analysis.',
  // Section 2 — pick your read
  pickHeading: 'Pick your read',
  pickHelper: "After reading the analysis, pick the read you'd give.",
  feedbackNailedIt: 'Nailed it.',
  feedbackTryAnother: 'Not the strongest read. Try another.',
  judgmentCall: 'Judgment call — no single right answer here.',
  // Section 3 — your take
  takeHeading: 'Where do you agree with the analysis, and where do you see it differently?',
  takeHelper: 'Analysts push back; prompt-followers just paste. Be specific.',
  takePlaceholder: 'In one or two sentences: what did the analysis miss, or what would you push back on?',
  takeMaxChars: 500,
  revealCta: 'Reveal sample answer',
  // Section 4 — your name
  nameHeading: 'Your name for the analysis',
  nameHelper: "We'll use this to personalize your recommendations.",
  namePlaceholder: 'e.g., Maria Chen',
  // Bottom bar
  modulePill: 'Module 4 · Real company analysis',
  submitCta: 'Submit',
} as const;

/** m4-complete interstitial copy (Figma 40:9610). */
export const M4_COMPLETE = {
  headline: 'All modules completed',
  subtitle: 'Now, to your results...',
} as const;

// ---------------------------------------------------------------------------
// Step 2 — pick-your-read chip groups
// ---------------------------------------------------------------------------

export type M4GroupId = 'revenue' | 'margin' | 'riskOpp';

export type RevenueChipId = 'accel' | 'decel' | 'steady' | 'mixed_rev';
export type MarginChipId = 'margin_up' | 'margin_down' | 'margin_flat' | 'mixed_margin';
export type RiskOppChipId = 'margin_opp' | 'rev_risk' | 'comp_pressure' | 'op_lev';
export type M4ChipId = RevenueChipId | MarginChipId | RiskOppChipId;

export interface M4Chip {
  id: M4ChipId;
  label: string;
}

export interface M4Group {
  chips: readonly M4Chip[];
  /** Whether this row is objectively gradeable (revenue/margin) vs interpretive (riskOpp). */
  graded: boolean;
  id: M4GroupId;
  label: string;
}

export const M4_GROUPS: readonly M4Group[] = [
  {
    id: 'revenue',
    label: 'Revenue story',
    graded: true,
    chips: [
      { id: 'accel', label: 'Accelerating growth' },
      { id: 'decel', label: 'Decelerating growth' },
      { id: 'steady', label: 'Steady / stable' },
      { id: 'mixed_rev', label: 'Volatile / mixed signals' },
    ],
  },
  {
    id: 'margin',
    label: 'Margin story',
    graded: true,
    chips: [
      { id: 'margin_up', label: 'Margins improving' },
      { id: 'margin_down', label: 'Margins compressing' },
      { id: 'margin_flat', label: 'Margins stable' },
      { id: 'mixed_margin', label: 'Mixed across years' },
    ],
  },
  {
    id: 'riskOpp',
    label: 'Biggest risk or opportunity',
    graded: false,
    chips: [
      { id: 'margin_opp', label: 'Margin expansion opportunity' },
      { id: 'rev_risk', label: 'Revenue concentration risk' },
      { id: 'comp_pressure', label: 'Competitive / market pressure' },
      { id: 'op_lev', label: 'Operating leverage opportunity' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Answer key — revenue + margin are graded; riskOpp is the "intended" read only.
// ---------------------------------------------------------------------------

export interface M4AnswerKey {
  margin: MarginChipId;
  revenue: RevenueChipId;
  /** Intended analyst read — interpretive, never graded red. */
  riskOpp: RiskOppChipId;
}

export const M4_PICK_ANSWER_KEY: Record<CompanyId, M4AnswerKey> = {
  spotify: { revenue: 'decel', margin: 'margin_up', riskOpp: 'margin_opp' },
  airbnb: { revenue: 'decel', margin: 'margin_flat', riskOpp: 'comp_pressure' },
  shopify: { revenue: 'accel', margin: 'margin_up', riskOpp: 'op_lev' },
  nvidia: { revenue: 'decel', margin: 'mixed_margin', riskOpp: 'rev_risk' },
  duolingo: { revenue: 'steady', margin: 'margin_up', riskOpp: 'op_lev' },
};

// ---------------------------------------------------------------------------
// Step 1 — streamed sample analyses (one per company; ~3 bullets each).
// ---------------------------------------------------------------------------

export const SAMPLE_ANALYSES: Record<CompanyId, string> = {
  spotify:
    '• Revenue growth is decelerating. €13.2B to €15.7B to €17.2B is +19% then +10%, so the FY25 leg is roughly half the pace of FY24. Price increases and subscriber gains are still landing (290M Premium subs, +10%), but the top-line is maturing and a strong euro is now a real headwind on reported growth.\n\n' +
    '• The bigger story is the profit turn. Operating income went from -€0.4B in FY23 to +€1.4B in FY24 to +€2.2B in FY25, the first full-year operating profit landing in FY24 and roughly doubling the year after. Opex actually fell (€3.8B to €3.3B) after the 2023 layoffs and podcast trim while gross profit grew €2.1B, which is operating leverage finally showing up after years of "trust us."\n\n' +
    "• Gross margin climbed from 25.6% to 30.1% to 32.0% as the ad business and marketplace programs improved the mix, but it is still capped by label licensing because Spotify doesn't own the music. Risk: those renewals limit how high gross margin can ultimately climb. Opportunity: audiobooks and podcast/ad inventory at marketplace-level take rates carry no royalty, so that is where margin can keep re-rating the equity story.",

  airbnb:
    '• Revenue growth keeps decelerating (+12% then +10%) but the profitability story is louder than the growth story. Operating margin jumped from 28% to 32% in FY24 and then held near 32% in FY25, while opex grew only modestly ($5.4B to $6.3B against $2.3B of revenue added over the two years).\n\n' +
    '• Marketplace economics are doing exactly what they should: gross margin holds near 83%, take rate is stable, and S&M is roughly flat as a percent of revenue. This is what a mature two-sided marketplace looks like once the network is built and the heavy spend is behind it. The margin gains have largely been banked, not extended, which is the honest read of a business near its profitability ceiling.\n\n' +
    '• Risk: short-term-rental regulation in major cities is the structural cap, and it is what keeps the margin from climbing further. New York already showed what that looks like. Opportunity: Experiences, Services, and the new hotel pilots, where Airbnb is opening fresh demand without leaning harder on the regulated core.',

  shopify:
    '• Revenue re-accelerated: $7.1B to $8.9B to $11.6B, a two-year jump of 63%, and the FY25 leg (+30%) is faster than FY24 (+25%). Shopify itself flagged "30% revenue growth, four points higher than 2024," so this is acceleration off an already-large base, not a fade.\n\n' +
    '• The deeper story is sustained operating leverage. After the 2023 logistics divestiture cleaned up the cost base, operating income kept climbing: $1.1B to $1.9B to $2.6B, with operating margin going 15.5% to 21.3% to 22.4%. Opex grew slower than revenue (gross profit grew $2.8B over two years while opex added only $1.3B), so the leverage from the restructuring is now compounding rather than a one-time reset.\n\n' +
    "• Risk: Shopify's SMB merchant base is the most recession-sensitive customer segment in software, and the gross-margin mix is drifting as lower-margin Payments takes a bigger share of revenue. Opportunity: Shopify Plus and enterprise, with higher take rates, lower churn, and a moat that smaller merchants don't get to vote on.",

  nvidia:
    "• The numbers still don't look normal. Revenue went $61B to $130B to $216B, about 3.5x in two years, and operating income went $33B to $82B to $130B, roughly 3.9x. This is what controlling the substrate of an entire AI infrastructure cycle does to one income statement. But notice the percentage: growth went +113% then +66%. Decelerating in rate, still enormous in absolute dollars ($86B added in the last year alone).\n\n" +
    '• The margin story turned mixed this year. Gross margin climbed 72% to 75% through FY25, then slipped to 71% in FY26 after a $4.5B charge tied to H20 chips stranded by U.S.-China export controls. Operating margin tells the same shape: 54% to 63% to 60%, up then down. Opex only grew $11B to $23B against $155B of incremental revenue, so the operating leverage is real, but the FY26 dip is a reminder that the margin line is now exposed to policy, not just product.\n\n' +
    "• Risk: customer and geographic concentration. A handful of hyperscalers are most of the revenue and they're all building their own silicon, while export controls just cost billions in a single quarter and capped China access. Opportunity: inference workloads at non-cloud enterprises are only starting to ramp, and that base is an order of magnitude larger than training.",

  duolingo:
    '• Revenue growth is high and steady, not accelerating. Duolingo went $531M to $748M to $1,038M (+41% then +39%) and crossed a billion dollars in revenue for the first time in FY25. The two growth legs are nearly identical, so the read is durable ~40% growth rather than an inflection. The dollars added kept climbing ($217M then $290M), which is the part that actually matters at this scale.\n\n' +
    '• The real story is operating margin, which went from -2% to +8% to +13% as the company crossed into operating profitability in FY24 and extended the lead in FY25. Operating income went -$13M to +$63M to +$136M. Revenue grew +39% in FY25 while operating expenses grew +27% ($481M to $613M), and that gap is the operating leverage finally showing up on the income statement.\n\n' +
    '• Gross margin did not improve, it slipped slightly, from about 73% to about 72%, as AI and compute costs rose with the push into generative features. So the margin story lives entirely in the operating line, not the gross line. Note one trap: FY25 net income was $414M, but most of that is a one-time deferred-tax valuation-allowance release, not the business earning more, which is why operating income ($136M) is the honest number to anchor on.\n\n' +
    "• Risk: AI tutors, including Duolingo's own, could cannibalize premium subscriptions if the free tier gets too good. Opportunity: the Duolingo English Test is a B2B revenue line with structurally higher margins than the consumer app, and it is still small enough to compound for years.",
};

// ---------------------------------------------------------------------------
// Step 3 — "your take" sample answers, auto-filled on Reveal (optional field).
// ---------------------------------------------------------------------------

export const TAKE_SAMPLES: Record<CompanyId | '_default', string> = {
  nvidia:
    'I agree the operating leverage is real, but the analysis under-weights customer-concentration risk. A handful of hyperscalers building their own silicon is the structural cap on the upside.',
  spotify:
    "I agree on the operating-leverage turnaround, but I'd push back on how durable it is. Gross margin is capped by label licensing, so the audiobook/ads mix is what really has to carry it.",
  airbnb:
    'I agree the profitability story is stronger than the growth story, but the analysis glosses over regulation. Short-term-rental rules in big cities are the real structural cap, not demand.',
  shopify:
    "I agree the operating leverage is durable now that the logistics cleanup is behind them, but I'd add that the SMB merchant base makes revenue more recession-sensitive than the clean op-income line suggests.",
  duolingo:
    "I agree the operating-leverage inflection is the headline, but I'd flag that the same AI tutors driving engagement could cannibalize premium subscriptions if the free tier gets too good.",
  _default:
    "I agree with the core read, but I'd push back on how lightly the analysis treats the biggest structural risk in these numbers. That's where the real debate is.",
};
